import { useMemo, useEffect } from 'react';
import theme from 'App.theme';
import { useOrder } from 'api/hooks/useOrder';
import { useCheckInTraysBySet } from 'hooks/useCheckInTraysBySet';
import { DeliveryChip } from 'components/DeliveryChip';
import { useLanguage } from 'context/AppContext';
import { TrayInspectionInfo } from 'api/models/Trays';
import { useAddSetContinuation } from 'hooks/useAddSetContinuation';

// Master switch for tray-number pre-population; may become a per hospital-location setting.
export const PREPOPULATE_TRAY_NUMBERS = true;

// One past the highest unavailable number; gaps are not filled (1, 2, 4 yields 5).
export const getNextAvailableTrayNumber = (unavailableTrayNumbers: number[]): number =>
  unavailableTrayNumbers.length ? Math.max(...unavailableTrayNumbers) + 1 : 1;

type AnyTray = {
  trayReference?: number | null;
  decantedTrays?: AnyTray[];
  inspections?: TrayInspectionInfo[];
};

type UseUnavailableTrayNumbersProps = {
  selectedNumber?: number | string | null;
  editedValues?: Record<number, string>;
  orderRecordId?: string;
};

// standalone so callers can compute the next available tray number without mounting the dialog UI
export const useUnavailableTrayNumbers = ({
  selectedNumber,
  editedValues,
  orderRecordId,
}: UseUnavailableTrayNumbersProps): { unavailableTrayNumbers: number[]; isLoading: boolean } => {
  const [checkInTraysBySet] = useCheckInTraysBySet();
  const [addSetContinuation] = useAddSetContinuation();
  const isAddSetContinuation = addSetContinuation?.deliveryId === orderRecordId;
  const { data: order, isLoading } = useOrder(isAddSetContinuation ? undefined : orderRecordId);

  const unavailableTrayNumbers = useMemo(() => {
    const sets = isAddSetContinuation ? [] : Object.values(order?.sets || {}).flat();
    const checkInSets = Object.values(checkInTraysBySet || {});
    const allSets = [...sets, ...checkInSets];

    const trayNumbersUnique = allSets.reduce<Set<number>>((acc, set) => {
      const trays = (Array.isArray(set.trays) ? set.trays : Object.values(set.trays || {})) as AnyTray[];

      const traysWithDecants = trays.flatMap(tray => {
        const inspection = tray.inspections?.find(i => i.stage == 'CHECK_IN');
        return (
          inspection?.decantedTrays?.length
            ? inspection.decantedTrays
            : tray.decantedTrays?.length
            ? tray.decantedTrays
            : [tray]
        ) as AnyTray[];
      });

      const trayNumbersInSet = traysWithDecants.reduce<Set<number>>((accTrays, tray: AnyTray) => {
        if (tray.trayReference != null) {
          const trayReference = editedValues?.[tray.trayReference] || tray.trayReference;
          accTrays.add(+trayReference);
        }
        return accTrays;
      }, new Set<number>());

      return new Set([...acc, ...trayNumbersInSet]);
    }, new Set<number>(isAddSetContinuation ? addSetContinuation?.unavailableTrayNumbers ?? [] : []));

    const selected = selectedNumber == null ? null : +selectedNumber;
    return [...trayNumbersUnique].filter(n => selected === null || n !== selected).sort((a, b) => a - b);
  }, [addSetContinuation, checkInTraysBySet, editedValues, isAddSetContinuation, order?.sets, selectedNumber]);

  return { unavailableTrayNumbers, isLoading: isAddSetContinuation ? false : isLoading };
};

type Props = {
  selectedNumber?: number | string | null;
  editedValues?: Record<number, string>;
  orderRecordId?: string;
  currentTrayNumber?: number | number[] | null;
  onUnavailableNumbersChange: (numbers: number[]) => void;
};

export const UnavailableTrayNumbers = ({
  selectedNumber,
  editedValues,
  orderRecordId,
  currentTrayNumber,
  onUnavailableNumbersChange,
}: Props) => {
  const lang = useLanguage()[0].TrayNumberDialog;
  const { unavailableTrayNumbers } = useUnavailableTrayNumbers({ selectedNumber, editedValues, orderRecordId });

  const currentTrayNumbers = useMemo(() => {
    return currentTrayNumber == null ? [] : Array.isArray(currentTrayNumber) ? currentTrayNumber : [currentTrayNumber];
  }, [currentTrayNumber]);

  useEffect(() => {
    onUnavailableNumbersChange(unavailableTrayNumbers);
  }, [unavailableTrayNumbers, onUnavailableNumbersChange]);

  if (!unavailableTrayNumbers.length) {
    return null;
  }

  return (
    <div style={{ marginBottom: theme.layout.spacing.large }}>
      <div>{lang.unavailableNumbers}:</div>
      <div
        style={{
          marginTop: theme.layout.spacing.small,
          display: 'flex',
          gap: theme.layout.spacing.small,
          flexWrap: 'wrap',
        }}
      >
        {unavailableTrayNumbers.map(trayNumber => (
          <DeliveryChip
            key={`tray-number-${trayNumber}`}
            variant={currentTrayNumbers.includes(trayNumber) ? 'express' : 'tray'}
            width="21px"
            height="21px"
          >
            {trayNumber}
          </DeliveryChip>
        ))}
      </div>
    </div>
  );
};
